"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { BookBookmark, ArrowRight, Spinner, ArrowsLeftRight, Clock } from "@phosphor-icons/react";
import { getTodaysReading, type TodaysReading } from "@/lib/get-todays-reading";
import { translatePassage } from "@/data/bible-book-names";
import type { Locale } from "@/i18n/config";

interface VerseData {
  text: string;
  reference: string;
  translation: string;
}

// English translations
type EnglishTranslation = 'kjv' | 'web';
// French translations
type FrenchTranslation = 'lsg' | 'ost';
// All supported translations
type BibleTranslation = EnglishTranslation | FrenchTranslation;

// Translation display names
const TRANSLATION_LABELS: Record<BibleTranslation, string> = {
  kjv: 'KJV',
  web: 'WEB',
  lsg: 'LSG',
  ost: 'OST',
};

// Translation full names for tooltips/accessibility
const TRANSLATION_FULL_NAMES: Record<BibleTranslation, string> = {
  kjv: 'King James Version',
  web: 'World English Bible',
  lsg: 'Louis Segond 1910',
  ost: 'Ostervald',
};

// Map each locale to a pair of Bible translation IDs.
// `/api/bible/route.ts` currently supports kjv/web (English) and lsg/ost (French).
// Spanish and Creole have no Spanish/Creole translation in the upstream API yet, so:
//   - es     → English KJV/WEB (neutral fallback until a Spanish source is wired up).
//   - creole → French LSG/OST (most Haitian Protestants in this congregation are
//     bilingual French; closer than English).
// When new translations are added to the Bible API route, extend the BibleTranslation
// type above and update this map.
const BIBLE_TRANSLATIONS: Record<Locale, [BibleTranslation, BibleTranslation]> = {
  en: ['kjv', 'web'],
  fr: ['lsg', 'ost'],
  es: ['kjv', 'web'],
  creole: ['lsg', 'ost'],
};

function getTranslationsForLocale(locale: Locale): [BibleTranslation, BibleTranslation] {
  return BIBLE_TRANSLATIONS[locale] ?? BIBLE_TRANSLATIONS.en;
}

async function fetchVerse(passage: string, translation: BibleTranslation): Promise<VerseData | null> {
  try {
    // Use our API route to avoid CORS issues
    const response = await fetch(
      `/api/bible?passage=${encodeURIComponent(passage)}&translation=${translation}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data;
  } catch {
    return null;
  }
}

interface CountdownTime {
  hours: number;
  minutes: number;
  seconds: number;
}

type DailyScriptureTranslations = {
  todaysScripture: string;
  nextReadingIn: string;
  hrs: string;
  min: string;
  sec: string;
  readingPlan: string;
  weeksFullBible: string;
  viewFullPlan: string;
  previewDisclaimer: string;
  planBeginsMessage: string;
  planCompletedMessage: string;
  restMessage: string;
};

interface DailyScriptureCardProps {
  onViewPlan: () => void;
  locale?: Locale;
  translations?: DailyScriptureTranslations;
}

function getTimeUntilMidnightToronto(): CountdownTime {
  const now = new Date();
  // Get current time in Toronto
  const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));

  // Calculate midnight Toronto time
  const midnightToronto = new Date(torontoTime);
  midnightToronto.setDate(midnightToronto.getDate() + 1);
  midnightToronto.setHours(0, 0, 0, 0);

  // Get the difference
  const diff = midnightToronto.getTime() - torontoTime.getTime();

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { hours, minutes, seconds };
}

// Custom hook for countdown timer - client-side only to avoid hydration mismatch
function useCountdown() {
  // Use a stable initial value that matches what server renders
  const [time, setTime] = useState<CountdownTime & { mounted: boolean }>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    mounted: false
  });

  useEffect(() => {
    // Use requestAnimationFrame to defer the initial state update
    // This avoids the lint error about synchronous setState in effects
    const rafId = requestAnimationFrame(() => {
      setTime({ ...getTimeUntilMidnightToronto(), mounted: true });
    });

    const interval = setInterval(() => {
      setTime(prev => ({ ...getTimeUntilMidnightToronto(), mounted: prev.mounted }));
    }, 1000);

    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(interval);
    };
  }, []);

  return time;
}

export function DailyScriptureCard({ onViewPlan, locale = 'en', translations }: DailyScriptureCardProps) {
  // Default translations for backwards compatibility
  const t: DailyScriptureTranslations = translations || {
    todaysScripture: "Today's Scripture",
    nextReadingIn: "Next Reading In (EST)",
    hrs: "hrs",
    min: "min",
    sec: "sec",
    readingPlan: "Reading Plan",
    weeksFullBible: "53 Weeks • Full Bible",
    viewFullPlan: "View Full Plan",
    previewDisclaimer: "This is a preview of the opening verses. Your full reading assignment is shown above.",
    planBeginsMessage: "The reading plan begins January 1, 2026",
    planCompletedMessage: "You've completed the reading plan!",
    restMessage: "Rest in His presence today",
  };

  // Get available translations based on locale
  const availableTranslations = getTranslationsForLocale(locale);

  // Initialize with lazy initializer to avoid useEffect
  const [todaysReading] = useState<TodaysReading>(() => getTodaysReading());
  const [verse, setVerse] = useState<VerseData | null>(null);
  const [translationState, setTranslation] = useState<BibleTranslation>(availableTranslations[0]);
  const [verseIndex, setVerseIndex] = useState(0);
  // Track which passage+translation combo we're fetching to derive loading state
  const [fetchKey, setFetchKey] = useState<string | null>(null);

  // Use custom hook for countdown
  const countdown = useCountdown();

  // Ensure the translation is valid for the current locale (derived state)
  // If the stored translation is not valid for the locale, use the first available one
  const translation: BibleTranslation = availableTranslations.includes(translationState)
    ? translationState
    : availableTranslations[0];

  // Derive loading state from whether we have a pending fetch
  const currentKey = todaysReading?.passages?.[verseIndex]
    ? `${todaysReading.passages[verseIndex]}-${translation}`
    : null;
  const isLoading = currentKey !== null && (fetchKey !== currentKey || verse === null);

  // Fetch verse when dependencies change
  useEffect(() => {
    if (!todaysReading?.passages?.length) return;

    let cancelled = false;
    const passage = todaysReading.passages[verseIndex] || todaysReading.passages[0];
    const key = `${passage}-${translation}`;

    fetchVerse(passage, translation).then((data) => {
      if (!cancelled) {
        setVerse(data);
        setFetchKey(key);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [todaysReading, translation, verseIndex]);

  const toggleTranslation = () => {
    const [first, second] = availableTranslations;
    setTranslation(t => t === first ? second : first);
  };

  // Split verse into words for animation
  const words = verse?.text?.split(' ') || [];

  // Format countdown with leading zeros
  const formatTime = (num: number) => num.toString().padStart(2, '0');

  return (
    <div className="relative w-full max-w-[500px]">
      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative overflow-hidden rounded-[2rem] bg-white dark:bg-[#0a0a0a] border border-zinc-200 dark:border-transparent shadow-xl dark:shadow-none"
      >
        {/* Noise Texture Overlay */}
        <div
          className="absolute inset-0 opacity-[0.3] dark:opacity-[0.4] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Ambient Glow - Light mode uses brand-red, dark uses amber */}
        <div className="absolute -top-32 -right-32 w-64 h-64 bg-brand-red/10 dark:bg-amber-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-brand-gold/10 dark:bg-rose-500/10 rounded-full blur-[100px]" />

        {/* Content */}
        <div className="relative z-10 p-8 md:p-10">
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <motion.div
                className="flex items-center gap-2 mb-2"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-brand-red dark:bg-amber-400 animate-pulse" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 dark:text-white/40">
                  {todaysReading?.dayLabel} • Week {todaysReading?.week}
                </span>
              </motion.div>
              <motion.h3
                className="text-zinc-900 dark:text-white/90 font-serif text-2xl md:text-3xl italic"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {t.todaysScripture}
              </motion.h3>
            </div>

            {/* Translation Toggle */}
            <motion.button
              onClick={toggleTranslation}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 hover:bg-zinc-200 dark:hover:bg-white/10 transition-all group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={TRANSLATION_FULL_NAMES[translation]}
            >
              <ArrowsLeftRight size={14} className="text-zinc-400 dark:text-white/40 group-hover:text-brand-red dark:group-hover:text-amber-400 transition-colors" />
              <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 dark:text-white/60">
                {TRANSLATION_LABELS[translation]}
              </span>
            </motion.button>
          </div>

          {/* Reading Reference Pills */}
          {todaysReading?.passages && todaysReading.passages.length > 0 && (
            <motion.div
              className="flex flex-wrap gap-2 mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {todaysReading.passages.map((passage, idx) => (
                <button
                  key={idx}
                  onClick={() => setVerseIndex(idx)}
                  className={`px-4 py-2 rounded-full text-xs font-bold tracking-wide transition-all ${
                    verseIndex === idx
                      ? 'bg-brand-red dark:bg-gradient-to-r dark:from-amber-500 dark:to-orange-500 text-white dark:text-black'
                      : 'bg-zinc-100 dark:bg-white/5 text-zinc-600 dark:text-white/60 hover:bg-zinc-200 dark:hover:bg-white/10 hover:text-zinc-800 dark:hover:text-white/80'
                  }`}
                >
                  {translatePassage(passage, locale)}
                </button>
              ))}
            </motion.div>
          )}

          {/* Verse Display */}
          <div className="min-h-[200px] flex items-center">
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full flex justify-center py-12"
                >
                  <Spinner size={32} className="text-brand-red dark:text-amber-400 animate-spin" />
                </motion.div>
              ) : verse ? (
                <motion.div
                  key={`${verse.reference}-${translation}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  {/* Opening Quote Mark */}
                  <motion.span
                    className="block text-8xl text-brand-red/30 dark:text-amber-500/40 leading-none -mb-6"
                    style={{ fontFamily: 'var(--font-script), cursive' }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    &ldquo;
                  </motion.span>

                  {/* Animated Verse Text */}
                  <p className="text-2xl md:text-3xl text-zinc-800 dark:text-white/90 leading-relaxed" style={{ fontFamily: 'var(--font-script), cursive' }}>
                    {words.map((word, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 0.2 + i * 0.03,
                          duration: 0.4,
                          ease: "easeOut"
                        }}
                        className="inline-block mr-[0.3em]"
                      >
                        {word}
                      </motion.span>
                    ))}
                  </p>

                  {/* Reference */}
                  <motion.p
                    className="text-sm font-mono text-brand-red dark:text-amber-400/80 tracking-wide"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + words.length * 0.03 }}
                  >
                    — {verse.reference} ({verse.translation})
                  </motion.p>

                  {/* Preview Disclaimer */}
                  <motion.p
                    className="text-[11px] text-zinc-400 dark:text-white/40 italic mt-4 leading-relaxed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 + words.length * 0.03 }}
                  >
                    {t.previewDisclaimer}
                  </motion.p>
                </motion.div>
              ) : (
                <motion.p
                  key="no-verse"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-zinc-400 dark:text-white/40 text-2xl"
                  style={{ fontFamily: 'var(--font-script), cursive' }}
                >
                  {todaysReading?.isBeforePlanStart
                    ? t.planBeginsMessage
                    : todaysReading?.isAfterPlanEnd
                    ? t.planCompletedMessage
                    : t.restMessage}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Countdown Timer */}
          <motion.div
            className="mt-8 p-4 rounded-2xl bg-zinc-50 dark:bg-white/5 border border-zinc-100 dark:border-white/5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-zinc-400 dark:text-white/40" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 dark:text-white/40">
                  {t.nextReadingIn}
                </span>
              </div>
              <div className="flex items-center gap-1 font-mono">
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
                    {countdown.mounted ? formatTime(countdown.hours) : '--'}
                  </span>
                  <span className="text-[8px] uppercase text-zinc-400 dark:text-white/30">{t.hrs}</span>
                </div>
                <span className="text-2xl font-bold text-zinc-300 dark:text-white/20 -mt-3">:</span>
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
                    {countdown.mounted ? formatTime(countdown.minutes) : '--'}
                  </span>
                  <span className="text-[8px] uppercase text-zinc-400 dark:text-white/30">{t.min}</span>
                </div>
                <span className="text-2xl font-bold text-zinc-300 dark:text-white/20 -mt-3">:</span>
                <div className="flex flex-col items-center">
                  <span className="text-2xl font-bold text-brand-red dark:text-amber-400 tabular-nums">
                    {countdown.mounted ? formatTime(countdown.seconds) : '--'}
                  </span>
                  <span className="text-[8px] uppercase text-zinc-400 dark:text-white/30">{t.sec}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Footer Actions */}
          <motion.div
            className="flex items-center justify-between mt-6 pt-6 border-t border-zinc-100 dark:border-white/5"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-red/10 dark:bg-gradient-to-br dark:from-amber-500/20 dark:to-orange-500/20 flex items-center justify-center">
                <BookBookmark weight="fill" size={20} className="text-brand-red dark:text-amber-400" />
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 dark:text-white/30">{t.readingPlan}</p>
                <p className="text-sm font-bold text-zinc-700 dark:text-white/70">{t.weeksFullBible}</p>
              </div>
            </div>

            <motion.button
              onClick={onViewPlan}
              className="group flex items-center gap-2 px-5 py-3 rounded-full bg-brand-red dark:bg-gradient-to-r dark:from-amber-500 dark:to-orange-500 text-white dark:text-black font-bold text-xs uppercase tracking-wider hover:shadow-[0_0_30px_rgba(196,30,58,0.3)] dark:hover:shadow-[0_0_30px_rgba(245,158,11,0.3)] transition-all"
              whileHover={{ scale: 1.05, x: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              {t.viewFullPlan}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Decorative Elements */}
      <div className="absolute -top-4 -right-4 w-24 h-24 border border-brand-red/20 dark:border-amber-500/20 rounded-full pointer-events-none" />
      <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-zinc-200 dark:border-white/5 rounded-full pointer-events-none" />
    </div>
  );
}
